import { useId, useState } from "react";
import { useActiveOrganization, useAuth } from "../context/AuthContext";
import { ORGANIZATION_STATUS_LABELS } from "../lib/tenant-ui";
import { ApiError } from "../lib/api";
import { cmsStrings } from "../terminology";
import { LtrIsolate } from "./LtrIsolate";

/**
 * Active organization badge + selector (only when multiple selectable orgs / platform scope).
 * Server authorization remains authoritative; this only switches session context.
 */
export function TenantContextBar() {
  const { user, switchOrganization, switchingOrganization } = useAuth();
  const active = useActiveOrganization();
  const selectId = useId();
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const selectable = user.selectableOrganizations ?? [];
  const showSelector = selectable.length > 1;

  async function onSelect(organizationId: string) {
    if (!organizationId || organizationId === active.organizationId) return;
    setError(null);
    try {
      await switchOrganization(organizationId);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.status === 403
            ? "سازمان انتخاب‌شده در دسترس نیست"
            : err.message
          : "تغییر سازمان ناموفق بود";
      setError(message);
    }
  }

  const statusLabel = active.status ? ORGANIZATION_STATUS_LABELS[active.status] : null;

  return (
    <div
      className="tenant-context"
      aria-label={cmsStrings.shell.orgContextAria}
      data-vt-tenant-context
    >
      <div className="tenant-context__active">
        <span className="tenant-context__label">سازمان فعال</span>
        {active.displayName ? (
          <strong className="tenant-context__name">{active.displayName}</strong>
        ) : (
          <strong className="tenant-context__name tenant-context__name--missing">
            انتخاب نشده
          </strong>
        )}
        {active.code && (
          <LtrIsolate className="tenant-context__code" mono>
            {active.code}
          </LtrIsolate>
        )}
        {statusLabel && active.status && active.status !== "active" && (
          <span
            className={`tenant-context__status tenant-context__status--${active.status}`}
          >
            {statusLabel}
          </span>
        )}
      </div>

      {showSelector && (
        <div className="tenant-context__switch">
          <label htmlFor={selectId} className="visually-hidden">
            تغییر سازمان
          </label>
          <select
            id={selectId}
            className="tenant-context__select"
            value={active.organizationId ?? ""}
            disabled={switchingOrganization}
            onChange={(e) => void onSelect(e.target.value)}
            aria-busy={switchingOrganization}
          >
            {!active.organizationId && (
              <option value="" disabled>
                انتخاب سازمان…
              </option>
            )}
            {selectable.map((org) => (
              <option key={org.organizationId} value={org.organizationId}>
                {org.displayName}
                {org.status !== "active"
                  ? ` (${ORGANIZATION_STATUS_LABELS[org.status]})`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <p className="tenant-context__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function TenantContextBanners() {
  const { user } = useAuth();
  const active = useActiveOrganization();

  if (!user) return null;

  if (active.hasNoOrganizations) {
    return (
      <div className="tenant-banner tenant-banner--danger" role="status">
        هیچ سازمانی به حساب شما اختصاص داده نشده است. با مالک پلتفرم تماس بگیرید.
      </div>
    );
  }

  if (active.needsSelection || !active.organizationId) {
    return (
      <div className="tenant-banner tenant-banner--warning" role="status">
        لطفاً یک سازمان را از فهرست بالا انتخاب کنید. تا قبل از انتخاب، داده‌های
        سازمانی بارگذاری نمی‌شوند.
      </div>
    );
  }

  if (active.status === "suspended") {
    return (
      <div className="tenant-banner tenant-banner--warning" role="status">
        این سازمان معلق است. مشاهده مجاز است؛ ایجاد، انتشار و تولید بسته مسدود
        شده‌اند. احراز هویت مشتریان هات‌اسپات تحت تأثیر قرار نمی‌گیرد.
      </div>
    );
  }

  if (active.status === "archived") {
    return (
      <div className="tenant-banner tenant-banner--danger" role="status">
        این سازمان بایگانی شده است و فقط به‌صورت فقط‌خواندنی در دسترس است.
      </div>
    );
  }

  if (!active.writable) {
    return (
      <div className="tenant-banner tenant-banner--warning" role="status">
        سازمان فعال برای نوشتن در دسترس نیست.
      </div>
    );
  }

  return null;
}
